import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import { invoiceAPI, vendorAPI, categoryAPI } from '../api/api';

const EditInvoiceDialog = ({ open, onClose, invoice, onSave, onError }) => {
  const [formData, setFormData] = useState({
    total_amount: '',
    currency: '',
    invoice_date: '',
    payment_status: '',
    vendor_id: '',
    category_id: ''
  });
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [error, setError] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Load vendors and categories when dialog opens
  useEffect(() => {
    if (open) {
      loadOptions();
      if (invoice) {
        loadPdf();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice]);

  // Load vendors and categories
  const loadOptions = async () => {
    setLoadingOptions(true);
    setError('');
    
    try {
      const [vendorsResponse, categoriesResponse] = await Promise.all([
        vendorAPI.getVendors(),
        categoryAPI.getCategories()
      ]);
      
      setVendors(vendorsResponse.data);
      setCategories(categoriesResponse.data);
    } catch (err) {
      console.error('Error loading options:', err);
      let errorMessage = 'Failed to load vendors and categories';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      setError(errorMessage);
    } finally {
      setLoadingOptions(false);
    }
  };

  // Load PDF for the invoice
  const loadPdf = async () => {
    if (!invoice) return;
    
    setPdfLoading(true);
    try {
      const response = await invoiceAPI.getInvoicePdf(invoice.id);
      if (response && response.data) {
        const contentType = response.headers['content-type'];
        if (contentType && contentType.includes('application/pdf')) {
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          setPdfUrl(url);
        }
      }
    } catch (err) {
      console.error('Error loading PDF:', err);
      // We can continue even if PDF fails to load
    } finally {
      setPdfLoading(false);
    }
  };

  // Initialize form data when invoice changes
  useEffect(() => {
    if (invoice) {
      setFormData({
        total_amount: invoice.total_amount || '',
        currency: invoice.currency || '',
        invoice_date: invoice.invoice_date || '',
        payment_status: invoice.payment_status || '',
        vendor_id: invoice.vendor_id || '',
        category_id: invoice.category_id || ''
      });
    }
  }, [invoice]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (!invoice) return;
    
    setLoading(true);
    setError('');
    
    try {
      await invoiceAPI.updateInvoice(invoice.id, formData);
      onSave();
      onClose();
    } catch (err) {
      console.error('Error updating invoice:', err);
      let errorMessage = 'Failed to update invoice';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  if (!invoice) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle>
        Edit Invoice
      </DialogTitle>
      
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flex: 1,
          p: 0,
          overflow: 'hidden'
        }}
      >
        {error && (
          <Alert severity="error" sx={{ m: 2, width: '100%' }}>
            {error}
          </Alert>
        )}
        
        {/* PDF Viewer Section - Left Side */}
        <Box
          sx={{
            width: '50%',
            borderRight: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: '#ffffff'
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'auto',
              p: 2
            }}
          >
            {pdfLoading ? (
              <CircularProgress />
            ) : pdfUrl ? (
              <iframe 
                src={pdfUrl} 
                title="PDF Viewer" 
                width="100%" 
                height="100%" 
                style={{ border: 'none' }} 
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No PDF available
              </Typography>
            )}
          </Box>
        </Box>

        {/* Editable Data Section - Right Side */}
        <Box
          sx={{
            width: '50%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            backgroundColor: '#f5f5f5',
            p: 3
          }}
        >
          {loadingOptions ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : (
            <Box component="form" sx={{ pt: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>Invoice Details</Typography>
              
              <TextField
                autoFocus
                margin="dense"
                name="total_amount"
                label="Total Amount"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.total_amount}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="dense"
                name="currency"
                label="Currency"
                fullWidth
                variant="outlined"
                value={formData.currency}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="dense"
                name="invoice_date"
                label="Invoice Date"
                type="date"
                fullWidth
                variant="outlined"
                value={formData.invoice_date}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ mb: 2 }}
              />
              
              <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                <InputLabel>Payment Status</InputLabel>
                <Select
                  name="payment_status"
                  value={formData.payment_status}
                  label="Payment Status"
                  onChange={handleChange}
                >
                  <MenuItem value="Paid">Paid</MenuItem>
                  <MenuItem value="Unpaid">Unpaid</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                <InputLabel>Vendor</InputLabel>
                <Select
                  name="vendor_id"
                  value={formData.vendor_id}
                  label="Vendor"
                  onChange={handleChange}
                >
                  {vendors.map(vendor => (
                    <MenuItem key={vendor.id} value={vendor.id}>
                      {vendor.standard_name || vendor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category_id"
                  value={formData.category_id}
                  label="Category"
                  onChange={handleChange}
                >
                  {categories.map(category => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.standard_name || category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || loadingOptions}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditInvoiceDialog;